import React, { useEffect, useState } from "react";
import { Alert, Button, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { OptionListEditor, normaliseOptions, validateOptionList } from "./SignupSection";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { DEFAULT_CANCELLATION_REASONS, SignupOption } from "../../../model/client/ClientSettings";

const { Paragraph, Text } = Typography;

export interface CancellationSectionValue {
    reasons: SignupOption[];
}

export const DEFAULT_CANCELLATION: CancellationSectionValue = {
    reasons: DEFAULT_CANCELLATION_REASONS,
};

/**
 * Editor for the cancellation reason list offered when a membership is cancelled.
 *
 * Deliberately reuses the signup OptionListEditor: same value/label shape, same validation, and the
 * backend runs both through SignupSettings.TryCreateOptions, so a second implementation would only
 * drift.
 */
export const CancellationSection: React.FC<SectionPanelProps<CancellationSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => (
    <OptionListEditor
        title="Reason for Leaving"
        description="Offered as radio buttons when a membership is cancelled, and used to group the Retention Reporting breakdown. Changing a VALUE orphans cancellations already recorded under the old one — reword the label instead."
        testId="settings-cancellation-reasons"
        options={value.reasons}
        onChange={(reasons) => onChange({ ...value, reasons })}
        valuePlaceholder="MovingArea"
        labelPlaceholder="Moving area"
        readOnly={readOnly}
        error={validateOptionList(value.reasons)}
    />
);

export const CancellationSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<CancellationSectionValue | null>(null);
    const [value, setValue] = useState<CancellationSectionValue>(DEFAULT_CANCELLATION);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const loaded: CancellationSectionValue = {
                    reasons: config?.settings.cancellation?.reasons ?? DEFAULT_CANCELLATION.reasons,
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);
    const valid = validateOptionList(value.reasons) === null;

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !valid) return;
        setSaving(true);
        setSaveError(null);
        const saved: CancellationSectionValue = { reasons: normaliseOptions(value.reasons) };
        try {
            await service.updateCancellation(clientId, saved);
            setInitial(saved);
            setValue(saved);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-cancellation-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Controls the reasons an operator can pick when cancelling a membership.
            </Paragraph>
            <CancellationSection value={value} onChange={setValue} />
            {saveError && <Alert type="error" message={saveError} data-testid="settings-cancellation-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-cancellation-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-cancellation-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
